/* lp-video-buy — <lp-video-buy>: families of products beside the clips that sell them.
   Data: the JSON island (families → fits → products → colourways → variants), one Panel per family. Panel state:
   fit, colourway (product handle + colour), size (none pre-picked; kept across fits and colours, so the colour rows
   sorted by it stay put), opt (a product's third option, e.g. Absorbency: the island carries its name and values;
   the first value in stock is pre-picked and kept across fits and colours; a row of pills after the sizes, a value
   with no stock in the picked size slashed; variants match on size and opt), quantity.
   Desktop stage: one <video>, swapped to the family whose panel crosses the middle of the screen (veil wipe with
   GSAP); muted autoplay when on screen, "Tap for sound" unmutes. Mobile clip cards: the card of the family on
   screen plays muted; tapping opens the viewer <dialog> with sound. Nothing downloads before it is needed.
   Add to cart: /cart/add.js with Impact's bundled sections, then variant:add / cart:change / lp:cart-add (the
   lp-product-duo contract). The active family emits lp:selection {handle, title, label, url, colour, image, price} on every
   change it shows and when the active family changes (the set-switcher payload; lp-sticky-cta follows it, 13/10/2026). Motion with GSAP core (LP.gsap) and motion allowed: veil wipe, fit thumb and swatch
   ring glides, body fade on a fit change, and on desktop section paging: each family is one screen tall and one
   wheel or arrow gesture moves exactly one family, eased slow → fast → slow (power2.inOut, 0.9s); the page then
   rests until the next gesture. Outside the section scrolling is native. Without GSAP or with reduced motion
   every change is instant and scrolling is native.
   Packs (data-tiers, e.g. 1,4,7): a card per pack replaces the quantity stepper. Each card shows the unit price the
   cart plus that pack earns under the store's cart-value discount (data-value-tiers "120:10,200:15"; the same
   maths as the product page: pct of cart subtotal + count × price, unit rounded to the cent). The subtotal comes
   from Liquid and follows cart:change. "Build your own pack" (pack of 2+): slots, one per pair, and the builder's own
   colour row (fit tabs when there are several fits, so styles mix); tapping a colour adds it to the next slot,
   tapping a slot removes it; one size for the pack, or (data-builder-sizes "pair") each pair keeps the size picked
   when it was added; each pair also keeps the third option it was added with (a period pack can mix Moderate and
   Heavy, 13/10/2026); the pack goes in as one line per variant. The stepper counts pairs; the lit pack is the
   largest at or below it.
   Colour tint (01/10/2026, root .lp-video-buy--tint): --lp-video-buy-tint on the root takes the swatch hex of the
   colour on show in the active family (data-i swatch from the island); a colour without a hex clears it (plain).
   Socks add-on (01/10/2026, [data-lp-addon] in a family's form, snippet lp-video-buy-nudge): live cart (data-cart,
   follows cart:change) + this family's pairs against data-value-tiers; the card shows when the next tier is within
   the biggest pack's reach, a tier is earned, or data-lp-addon-always is set. The switch adds the chosen sock × pack
   to the same cart add.
   Fit photos (01/10/2026, root .lp-video-buy--fits-photos): the fit buttons are photo tiles (Liquid sets --lp-swatch)
   and [data-lp-fit-name] shows the fit on show; the builder's row lists every fit's colourways at once (no tabs) and
   the summary names the fits in the pack. While building, the pack cards price the pack's own mix (its slots, the
   empty ones at the previewed colour's price), and [data-lp-pack-total] shows what is in the pack after the tier.
   lp:product-change {handle} from outside (a hero card): the panel whose fit sells that product shows it.
   Particles (01/10/2026, rebuilt): Motif draws the family's `motif` (embers / bubbles / wisps / leaves) on one
   <canvas> in .lp-video-buy__motif — a few hundred dots on requestAnimationFrame, colours read from the element's CSS
   `color` / `border-color`, running only while the section is on screen (.is-on), the tab visible, motion allowed and
   on phones at 30 fps; the canvas is re-sized on ResizeObserver. Replaces the CSS layer stack, which the GPU dropped
   for single frames when the band's height animated and ran hot. */
(function () {
  'use strict';
  if (window.customElements.get('lp-video-buy')) return;

  function motionOK() { return window.LP ? window.LP.motionOK : !window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  /* reveal el sideways inside its scrolling strip only: scrollIntoView({ block: 'nearest' }) would also move the window */
  function revealInline(el, smooth) {
    var p = el.parentElement; if (!p) return;
    var pr = p.getBoundingClientRect(), r = el.getBoundingClientRect(), l = r.left - pr.left + p.scrollLeft, right = l + r.width, to = null;
    if (l < p.scrollLeft) to = l; else if (right > p.scrollLeft + p.clientWidth) to = right - p.clientWidth;
    if (to === null) return;
    if (p.scrollTo) p.scrollTo({ left: to, behavior: smooth ? 'smooth' : 'auto' }); else p.scrollLeft = to;
  }
  function money(cents, format) {
    var v = (cents / 100).toFixed(2);
    if (!format) return '$' + v;
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, format.indexOf('no_decimals') > -1 ? v.replace(/\.00$/, '') : v);
  }
  function shortSize(v) { v = String(v || ''); return /^(Small|Medium|Large)$/.test(v) ? v.charAt(0) : v; }
  function esc(v) { return String(v == null ? '' : v).replace(/[&<>"']/g, function (ch) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]; }); }
  /* products name sizes differently ("Large" on one print, "L" on the next): compare them short */
  function sameSize(a, b) { return !!a && !!b && shortSize(a) === shortSize(b); }
  var slice = Array.prototype.slice;

  /* ---------------- one family's buy panel ---------------- */
  function Panel(root, el, data) {
    this.root = root; this.el = el; this.fits = data.fits || [];
    this.fit = 0; this.cw = null; this.size = null; this.opt = null; this.qty = 1; this.slide = 0;
    this.pack = root.tierDefault || 1; this.building = false; this.slots = [];
    this.index = parseInt(el.dataset.lpFamily, 10) || 0;
    var self = this, host = el.querySelector('[data-lp-fits]'), srcs = slice.call(el.querySelectorAll('[data-lp-fit-src]'));
    /* fit buttons were rendered as blocks; move them into the toggle */
    srcs.forEach(function (b, i) {
      b.removeAttribute('data-lp-fit-src'); b.dataset.lpFit = i;
      if (b.hasAttribute('data-selected')) { self.fit = i; b.removeAttribute('data-selected'); }
      host.appendChild(b);
    });
    var row = el.querySelector('[data-lp-fit-row]'); if (row) row.hidden = srcs.length < 2;
    /* socks add-on: the sock products' island and the shopper's picks (style, colour, size, pack) */
    this.addonEl = el.querySelector('[data-lp-addon]');
    this.socks = [];
    if (this.addonEl) { try { this.socks = JSON.parse(this.addonEl.querySelector('[data-lp-addon-data]').textContent) || []; } catch (e) { this.socks = []; } }
    this.addon = { on: false, style: 0, colour: null, size: null, pack: 0 };
    if (this.socks.length) { var fv = this.socks[0].variants.filter(function (v) { return v.available; })[0] || this.socks[0].variants[0]; if (fv) { this.addon.colour = fv.colour; this.addon.size = fv.size; } }
    var mini = el.querySelector('[data-lp-mini-fits]');
    if (mini) {
      srcs.forEach(function (b, i) {
        var c = document.createElement('button');
        c.type = 'button'; c.className = 'lp-video-buy__mini-fit'; c.dataset.lpFit = i; c.setAttribute('role', 'tab');
        c.textContent = (b.querySelector('.lp-video-buy__fit-name') || b).textContent.trim();
        if (b.dataset.badge) { c.dataset.badge = b.dataset.badge; c.setAttribute('aria-label', c.textContent + ', ' + b.dataset.badge); }
        mini.appendChild(c);
      });
      mini.hidden = srcs.length < 2;
    }
    this.setFit(this.fit, true);
  }
  Panel.prototype = {
    fitData: function () { return this.fits[this.fit] || { products: [] }; },
    colourwaysOf: function (fi) {
      var out = [], f = this.fits[fi] || { products: [] };
      f.products.forEach(function (p) { p.cw.forEach(function (c) { out.push({ p: p, c: c, key: p.handle + '|' + c.colour, fi: fi }); }); });
      return out;
    },
    colourways: function () { return this.colourwaysOf(this.fit); },
    current: function () { var self = this; return this.colourways().filter(function (x) { return x.key === self.cw; })[0] || this.colourways()[0]; },
    variant: function () { return this.variantOf(this.current()); },
    /* any fit's colourway: a pack can mix fits (socks: crew, ankle, no show) */
    byKey: function (key) {
      for (var i = 0; i < this.fits.length; i++) { var x = this.colourwaysOf(i).filter(function (y) { return y.key === key; })[0]; if (x) return x; }
      return null;
    },
    sized: function (x) { return !!x && x.c.variants.some(function (v) { return v.size; }); },
    /* a product's third option ({ name, values }) or null */
    optOf: function (x) { return x && x.p.opt && x.p.opt.values && x.p.opt.values.length ? x.p.opt : null; },
    /* the opt a colourway sells in: the picked one when its product has it, else the first value with stock, else the
       first value — so a pack can mix a Thong (Light only) with Bikini Briefs (Moderate / Heavy) without the shopper
       going back to the Style row (13/10/2026) */
    optFor: function (x) {
      var o = this.optOf(x); if (!o) return null;
      if (this.opt && o.values.indexOf(this.opt) > -1) return this.opt;
      return o.values.filter(function (val) { return x.c.variants.some(function (v) { return v.opt === val && v.available; }); })[0] || o.values[0];
    },
    /* a variant matches an opt (default: the colourway's resolved one) when its product has one; a product without
       one ignores it */
    optOk: function (x, v, opt) { if (!this.optOf(x)) return true; if (opt === undefined || opt === null) opt = this.optFor(x); return v.opt === opt; },
    /* the opt to show for a colourway: the picked one, else the first value with stock, else the first value */
    settleOpt: function (x) {
      var self = this, o = this.optOf(x); if (!o) return;
      if (this.opt && o.values.indexOf(this.opt) > -1) return;
      this.opt = o.values.filter(function (val) { return x.c.variants.some(function (v) { return v.opt === val && v.available; }); })[0] || o.values[0];
    },
    /* a colourway's variant in a size (default: the picked one) and an opt (default: the resolved one), or its only
       variant when it has no sizes */
    variantOf: function (x, size, opt) {
      var self = this;
      if (!x) return null; if (size === undefined) size = this.size;
      return x.c.variants.filter(function (v) { return sameSize(v.size, size) && self.optOk(x, v, opt); })[0] || (x.c.variants.length === 1 && !x.c.variants[0].size ? x.c.variants[0] : null);
    },
    canPack: function (x, size, opt) {
      if (!x) return false; if (size === undefined) size = this.size;
      var self = this, v = this.variantOf(x, size, opt);
      return size || !this.sized(x) ? !!(v && v.available) : x.c.variants.some(function (y) { return y.available && self.optOk(x, y, opt); });
    },
    /* builder slots are { k: colourway key, s: the pair's own size (builder_sizes "pair") or null (the pack's size),
       o: the pair's own opt (the colourway's resolved opt when it was added; a pack can mix Moderate and Heavy) } */
    perPair: function () { return this.root.dataset.builderSizes === 'pair'; },
    slotSize: function (sl) { return sl.s || this.size; },
    slotVariant: function (sl) { return this.variantOf(this.byKey(sl.k), this.slotSize(sl), sl.o); },
    slotOk: function (sl) { return this.canPack(this.byKey(sl.k), this.slotSize(sl), sl.o); },
    newSlot: function (key) { var x = this.byKey(key); return { k: key, s: this.perPair() ? this.size : null, o: x ? this.optFor(x) : null }; },
    /* unit price for a pack of n: the cart-value tier that cart + n units reaches (product page maths) */
    unitFor: function (n, price) { var pct = this.root.tierPct(this.root.cart + n * price); return { unit: Math.round(price * (1 - pct / 100)), pct: pct }; },
    /* a slot's price: its variant in its size, else the colourway's lowest */
    slotPrice: function (sl) {
      var v = this.slotVariant(sl), x = this.byKey(sl.k);
      return v ? v.price : (x ? Math.min.apply(null, x.c.variants.map(function (y) { return y.price; })) : 0);
    },
    /* building: the average unit price of the pack being built (empty slots at the previewed price), so the pack
       cards price the real mix when fits cost different amounts */
    packAverage: function (price) {
      var self = this, sum = 0, n = Math.max(this.pack, this.slots.length, 1);
      this.slots.forEach(function (sl) { sum += self.slotPrice(sl); });
      sum += Math.max(0, n - this.slots.length) * price;
      return Math.round(sum / n);
    },
    setPack: function (n) {
      this.pack = n;
      if (n < 2) { this.building = false; this.slots = []; }
      else if (this.building) this.slots = this.slots.slice(0, n);
      this.render('pack');
    },
    toggleBuild: function () {
      if (this.pack < 2) return;
      this.building = !this.building;
      var cur = this.current(), seed = this.building && (!this.perPair() || this.size || !this.sized(cur)) && this.canPack(cur);
      this.slots = seed ? [this.newSlot(this.cw)] : [];
      if (this.building) { this.packFit = this.fit; this.buildPackSwatches(); }
      this.render('pack');
    },
    /* building: a tap in the pack's own colour row previews that colour and drops it in the next free slot (with the
       picked size when each pair has its own) */
    pick: function (handle, colour) {
      var key = handle + '|' + colour, x = this.byKey(key);
      if (x && this.perPair() && this.sized(x) && !this.size) { this.shake('[data-lp-sizes]'); return; }
      if (!this.canPack(x)) { this.shake('[data-lp-pack-swatches]'); return; } /* can't join in this size */
      if (x.fi !== this.fit) this.markFit(x.fi);
      this.cw = key;
      if (this.slots.length >= this.pack) this.shake('[data-lp-slots]'); else this.slots.push(this.newSlot(key));
      this.render('colour'); this.placeRing(true);
    },
    unslot: function (i) { this.slots.splice(i, 1); this.render('pack'); },
    /* the builder's style tab also previews that style in the form (so its Colour / Size / third-option rows show what
       the next pair gets); the form's Style row moves the builder's tab the same way (13/10/2026) */
    setPackFit: function (i) { if (i === this.fit) { this.packFit = i; this.buildPackSwatches(); this.render('pack'); return; } this.setFit(i); },
    /* a colour row: colours that can't be bought in the picked size (or at all, before a size) dimmed and moved to the
       end, collection order (data-i) kept in each group. True when the order changed. */
    sortSwatches: function (host, attr) {
      if (!host) return false;
      var self = this, tiles = slice.call(host.querySelectorAll('[' + attr + ']')), ok = [], out = [];
      tiles.forEach(function (t) {
        var can = self.canPack(self.byKey(t.getAttribute(attr) + '|' + t.dataset.colour));
        if (!can && self.size && !t.hasAttribute('data-sold-out')) t.setAttribute('data-size-out', ''); else t.removeAttribute('data-size-out');
        (can ? ok : out).push(t);
      });
      var byI = function (m, n) { return m.dataset.i - n.dataset.i; }, order = ok.sort(byI).concat(out.sort(byI));
      if (!order.some(function (t, i) { return tiles[i] !== t; })) return false;
      order.forEach(function (t) { host.appendChild(t); });
      return true;
    },
    /* what the panel shows, for lp-sticky-cta and the like (the lp-set-switcher contract) */
    selection: function () {
      var x = this.current(); if (!x) return null;
      var v = this.variant(), price = v ? v.price : Math.min.apply(null, x.c.variants.map(function (y) { return y.price; }));
      return { handle: x.p.handle, title: x.p.title, label: this.fitData().label || '', url: x.p.url, colour: this.colourways().length > 1 ? x.c.colour : '', image: x.c.image || '', price: price };
    },
    shake: function (sel) { var s = this.el.querySelector(sel); if (!s) return; s.classList.remove('is-shake'); void s.offsetWidth; s.classList.add('is-shake'); },
    renderPack: function (price) {
      var self = this, el = this.el, fmt = this.root.format, many = this.fits.length > 1;
      /* the lit pack is the largest at or below the quantity (product page) */
      var lit = this.root.tiers.filter(function (n) { return n <= self.pack; }).sort(function (a, b) { return b - a; })[0];
      slice.call(el.querySelectorAll('[data-lp-tier]')).forEach(function (b) {
        var n = parseInt(b.dataset.lpTier, 10), u = self.unitFor(n, price);
        b.setAttribute('aria-pressed', n === lit ? 'true' : 'false');
        var unit = b.querySelector('[data-lp-tier-unit]'); if (unit) unit.textContent = money(u.unit, fmt);
        var off = b.querySelector('[data-lp-tier-off]');
        if (off) { off.hidden = !(u.pct > 0); off.textContent = u.pct > 0 ? (off.dataset.format || '[pct]% off').replace('[pct]', u.pct) : ''; }
      });
      /* bar display: the unit price for the quantity, struck original beside it */
      var ru = el.querySelector('[data-lp-readout-unit]'), rs = el.querySelector('[data-lp-readout-strike]');
      if (ru) { var uu = this.unitFor(this.pack, price); ru.textContent = money(uu.unit, fmt); ru.classList.toggle('is-discounted', uu.pct > 0); if (rs) { rs.hidden = !(uu.pct > 0); rs.textContent = uu.pct > 0 ? money(price, fmt) : ''; } }
      var build = el.querySelector('[data-lp-build]');
      if (build) { build.hidden = this.pack < 2; build.setAttribute('aria-pressed', this.building ? 'true' : 'false'); build.textContent = this.building ? build.dataset.close : build.dataset.open; }
      var pack = el.querySelector('[data-lp-pack]'), host = el.querySelector('[data-lp-slots]');
      if (pack) pack.hidden = !this.building;
      /* total of what is in the pack, after the tier the cart lands in with it */
      var tot = el.querySelector('[data-lp-pack-total]');
      if (tot) {
        var sum = 0; this.slots.forEach(function (sl) { sum += self.slotPrice(sl); });
        var tp = this.root.tierPct(this.root.cart + sum), after = Math.round(sum * (1 - tp / 100));
        tot.hidden = !(this.building && this.slots.length);
        var ts = tot.querySelector('[data-lp-pack-sum]'), tw = tot.querySelector('[data-lp-pack-was]');
        if (ts) ts.textContent = money(after, fmt);
        if (tw) { tw.hidden = !(tp > 0); tw.textContent = tp > 0 ? money(sum, fmt) : ''; }
      }
      if (host && this.building) {
        host.innerHTML = '';
        host.style.setProperty('--lp-video-buy-slots', Math.min(this.pack, 7));
        for (var i = 0; i < this.pack; i++) {
          var sl = this.slots[i], x = sl ? this.byKey(sl.k) : null, b = document.createElement(x ? 'button' : 'span');
          b.className = 'lp-video-buy__slot';
          if (x) {
            var sz = sl.s ? shortSize(sl.s) : '', so = sl.o && this.optOf(x) ? sl.o : '';
            b.type = 'button'; b.dataset.lpSlot = i;
            b.setAttribute('aria-label', 'Remove ' + [many ? (this.fits[x.fi] || {}).label : '', x.c.colour, sz, so].filter(Boolean).join(' '));
            if (x.c.image) b.style.setProperty('--lp-swatch', 'url(' + x.c.image.replace(/width=\d+/, 'width=160') + ')');
            if (!this.slotOk(sl)) b.setAttribute('data-sold-out', '');
            b.innerHTML = '<span class="lp-video-buy__slot-x" aria-hidden="true"></span>' + (sz ? '<span class="lp-video-buy__slot-size" aria-hidden="true">' + sz + '</span>' : '') + (so ? '<span class="lp-video-buy__slot-opt" aria-hidden="true">' + esc(so) + '</span>' : '');
          } else { b.setAttribute('aria-hidden', 'true'); }
          host.appendChild(b);
        }
      }
      /* the pack's colour row (and its fit tabs): how many of each are in the pack; colours that can't join (sold
         out, or out of stock in the picked size) dimmed and moved to the end, collection order kept in each group */
      var phost = el.querySelector('[data-lp-pack-swatches]');
      if (phost && this.building) {
        slice.call(el.querySelectorAll('[data-lp-pack-fit]')).forEach(function (t) { t.setAttribute('aria-selected', +t.dataset.lpPackFit === self.packFit ? 'true' : 'false'); });
        var counts = {}; this.slots.forEach(function (q) { counts[q.k] = (counts[q.k] || 0) + 1; });
        slice.call(phost.querySelectorAll('[data-lp-pick]')).forEach(function (t) { var n = counts[t.dataset.lpPick + '|' + t.dataset.colour] || 0; if (n) t.dataset.count = n; else t.removeAttribute('data-count'); });
        if (this.sortSwatches(phost, 'data-lp-pick')) phost.scrollLeft = 0;
        this.checkNext();
      }
    },
    items: function () {
      var self = this, out;
      if (!this.building) { var v = this.variant(); out = v ? [{ id: v.id, quantity: this.root.tiers ? this.pack : this.qty }] : []; }
      else {
        out = [];
        this.slots.forEach(function (sl) {
          var v = self.slotVariant(sl); if (!v) return;
          var m = out.filter(function (i) { return i.id === v.id; })[0];
          if (m) m.quantity += 1; else out.push({ id: v.id, quantity: 1 });
        });
      }
      var sv = this.sockVariant();
      if (out.length && this.addonShown && this.addon.on && sv && sv.available) out.push({ id: sv.id, quantity: this.packCount() });
      return out;
    },
    /* ---- socks add-on (the lp-set-switcher nudge) ---- */
    sock: function () { return this.socks[this.addon.style] || this.socks[0] || null; },
    packCount: function () { var b = this.addonEl ? this.addonEl.querySelectorAll('[data-lp-addon-pack]')[this.addon.pack] : null; return b ? parseInt(b.dataset.count, 10) || 1 : 1; },
    /* keep colour and size picks the chosen sock can honour */
    settleSock: function () {
      var sk = this.sock(), a = this.addon; if (!sk) return;
      var colours = sk.variants.filter(function (v) { return v.available; }).map(function (v) { return v.colour; });
      if (colours.indexOf(a.colour) < 0) a.colour = colours[0] || (sk.variants[0] && sk.variants[0].colour);
      var sizes = sk.variants.filter(function (v) { return v.colour === a.colour && v.available; }).map(function (v) { return v.size; });
      if (sizes.indexOf(a.size) < 0) a.size = sizes[0] || (sk.variants[0] && sk.variants[0].size);
    },
    sockVariant: function () {
      var sk = this.sock(), a = this.addon; if (!sk) return null;
      return sk.variants.filter(function (v) { return v.colour === a.colour && v.size === a.size; })[0] || null;
    },
    nextTier: function (cents) { var cur = this.root.tierPct(cents), out = null; (this.root.valueTiers || []).slice().sort(function (x, y) { return x.at - y.at; }).forEach(function (t) { if (!out && t.at > cents && t.pct > cur) out = t; }); return out; },
    /* the card: how far cart + this family's pairs are from the next tier; the socks that could close it; controls when on */
    renderAddon: function (price, anyStock) {
      var el = this.addonEl; if (!el) return;
      var sk = this.sock(), a = this.addon, self = this, root = this.root, fmt = root.format;
      var always = el.hasAttribute('data-lp-addon-always');
      var qty = root.tiers ? this.pack : this.qty;
      var base = root.cart + qty * price, basePct = root.tierPct(base), next = this.nextTier(base);
      var packs = slice.call(el.querySelectorAll('[data-lp-addon-pack]')), counts = packs.map(function (b) { return parseInt(b.dataset.count, 10) || 1; });
      var sv = this.sockVariant(), sockPrice = sv ? sv.price : (sk && sk.variants[0] ? sk.variants[0].price : 0);
      var reach = Math.max.apply(null, counts.concat([0])) * sockPrice;
      var count = counts[a.pack] || 1, on = a.on && sv && sv.available;
      var banner = el.querySelector('[data-lp-addon-banner]'), title = el.querySelector('[data-lp-addon-title]'), text = el.querySelector('[data-lp-addon-text]');
      var fill = function (f, amount, pct) { return String(f || '').replace('{amount}', String(amount)).replace('{pct}', String(pct)); };
      var state = null;
      if (next && sk && next.at - base <= reach) {
        var gap = next.at - (base + (on ? count * sv.price : 0));
        state = gap <= 0 ? { key: 'unlocked', txt: fill(text.dataset.lpUnlocked, 0, next.pct) } : { key: 'locked', txt: fill(text.dataset.lpAdd, Math.ceil(gap / 100), next.pct) };
      } else if (basePct > 0) state = { key: 'applied', txt: fill(text.dataset.lpApplied, 0, basePct) };
      var show = anyStock && !!sk && (always || !!state);
      el.hidden = !show; this.addonShown = show;
      if (!show) return;
      if (banner) {
        banner.hidden = !state;
        if (state) { banner.classList.toggle('is-unlocked', state.key !== 'locked'); title.textContent = title.dataset['lp' + state.key.charAt(0).toUpperCase() + state.key.slice(1)] || ''; text.textContent = state.txt; }
      }
      var row = el.querySelector('[data-lp-addon-row]'); if (row) row.hidden = false;
      el.classList.toggle('is-on', !!on);
      var tg = el.querySelector('[data-lp-addon-toggle]'), soldOut = !(sv && sv.available);
      if (tg) { tg.setAttribute('aria-checked', on ? 'true' : 'false'); tg.disabled = soldOut && !sk.variants.some(function (v) { return v.available; }); }
      var img = el.querySelector('[data-lp-addon-img]'), src = (sv && sv.image) || sk.image || '';
      if (img && img.getAttribute('src') !== src) { img.src = src; img.alt = sk.title; }
      /* the socks' price after the tier the order lands in with them added */
      var pct = root.tierPct(base + count * sockPrice), per = Math.round(sockPrice * (1 - pct / 100));
      var pe = el.querySelector('[data-lp-addon-price]'), ce = el.querySelector('[data-lp-addon-compare]');
      if (pe) pe.textContent = money(per, fmt);
      if (ce) { ce.hidden = per >= sockPrice; ce.textContent = money(sockPrice, fmt); }
      var styles = el.querySelector('[data-lp-addon-styles]');
      if (styles && styles.childElementCount !== this.socks.length) {
        styles.innerHTML = this.socks.map(function (x, i) { return '<button type="button" class="lp-video-buy__addon-tab" data-lp-addon-style="' + i + '" aria-pressed="false">' + esc(x.label || x.title) + '</button>'; }).join('');
        styles.hidden = this.socks.length < 2;
      }
      slice.call(el.querySelectorAll('[data-lp-addon-style]')).forEach(function (b, i) { b.setAttribute('aria-pressed', i === a.style ? 'true' : 'false'); });
      var tiles = el.querySelector('[data-lp-addon-colours]');
      if (tiles) {
        var seen = {}, cols = [];
        sk.variants.forEach(function (v) { if (v.colour && !seen[v.colour]) { seen[v.colour] = true; cols.push(v); } });
        tiles.innerHTML = cols.map(function (v) {
          var ok = sk.variants.some(function (w) { return w.colour === v.colour && w.available; });
          return '<button type="button" class="lp-video-buy__addon-tile" data-lp-addon-colour="' + esc(v.colour) + '" aria-label="' + esc(v.colour) + '" aria-pressed="' + (v.colour === a.colour ? 'true' : 'false') + '"' + (ok ? '' : ' disabled') + '>' + (v.image || sk.image ? '<img src="' + esc(v.image || sk.image) + '" alt="" width="48" height="48" loading="lazy">' : '') + '</button>';
        }).join('');
        tiles.hidden = cols.length < 2;
      }
      var sizes = el.querySelector('[data-lp-addon-sizes]');
      if (sizes) {
        var sz = sk.variants.filter(function (v) { return v.colour === a.colour; });
        sizes.innerHTML = sz.map(function (v) { return '<button type="button" class="lp-video-buy__size" data-lp-addon-size="' + esc(v.size) + '" aria-pressed="' + (v.size === a.size ? 'true' : 'false') + '"' + (v.available ? '' : ' disabled') + '>' + esc(shortSize(v.size)) + '</button>'; }).join('');
        sizes.hidden = sz.length < 2 || !sz[0].size;
      }
      packs.forEach(function (b, i) {
        b.setAttribute('aria-pressed', i === a.pack ? 'true' : 'false');
        var n = counts[i], pp = root.tierPct(base + n * sockPrice), each = Math.round(sockPrice * (1 - pp / 100)), pr = b.querySelector('[data-lp-addon-pack-price]');
        if (pr) pr.textContent = money(each, fmt) + (n > 1 ? ' each' : '');
      });
    },
    /* the previewed fit: its buttons and the main colour row (a pack being built keeps its slots) */
    markFit: function (i) {
      this.fit = i;
      slice.call(this.el.querySelectorAll('[data-lp-fits] [data-lp-fit], [data-lp-mini-fits] [data-lp-fit]')).forEach(function (b) { var k = parseInt(b.dataset.lpFit, 10); b.classList.toggle('is-active', k === i); b.setAttribute('aria-selected', k === i ? 'true' : 'false'); });
      this.buildSwatches();
    },
    setFit: function (i, first) {
      if (i < 0 || i >= this.fits.length) return;
      var keep = this.cw ? this.cw.split('|')[1] : null;
      this.markFit(i);
      var cws = this.colourways(), next = cws.filter(function (x) { return keep && x.c.colour === keep; })[0] || cws[0];
      this.cw = next ? next.key : null;
      this.packFit = i; this.buildPackSwatches();
      this.render(first ? 'instant' : 'fit');
      this.placeThumb(!first);
    },
    setColour: function (handle, colour, btn) { this.cw = handle + '|' + colour; this.render('colour'); this.placeRing(true, btn); },
    swatch: function (x) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'lp-video-buy__swatch'; b.dataset.colour = x.c.colour;
      var photo = this.root.dataset.swatchStyle === 'image' || !x.c.swatch;
      if (photo && x.c.image) b.style.setProperty('--lp-swatch', 'url(' + x.c.image.replace(/width=\d+/, 'width=120') + ')');
      else if (x.c.swatch) b.style.setProperty('--lp-swatch', x.c.swatch);
      if (!x.c.variants.some(function (v) { return v.available; })) b.setAttribute('data-sold-out', '');
      return b;
    },
    /* main colour row (data-lp-swatch): previews a colour of the current fit. The render sorts it (sortSwatches):
       colours without stock, in the picked size once there is one, at the end */
    buildSwatches: function () {
      var self = this, host = this.el.querySelector('[data-lp-swatches]'), ring = this.el.querySelector('[data-lp-ring]');
      slice.call(host.querySelectorAll('[data-lp-swatch]')).forEach(function (b) { b.remove(); });
      var cws = this.colourways();
      cws.forEach(function (x, i) { var b = self.swatch(x); b.dataset.lpSwatch = x.p.handle; b.dataset.i = i; b.setAttribute('aria-label', x.c.colour); host.appendChild(b); });
      var row = this.el.querySelector('[data-lp-colour-row]'); if (row) row.hidden = cws.length < 2;
      if (ring) ring.classList.remove('is-on');
      this.checkNext();
    },
    /* the builder's own row (data-lp-pick: adds to the pack) for its fit tab, and the tabs when there are several fits */
    buildPackSwatches: function () {
      var self = this, phost = this.el.querySelector('[data-lp-pack-swatches]'), tabs = this.el.querySelector('[data-lp-pack-fits]');
      if (!phost) return;
      if (this.packFit == null) this.packFit = this.fit;
      phost.innerHTML = '';
      /* fit photos: every fit's colourways in one row, named by fit (and colour when a fit has several) */
      var all = this.root.fitPhotos, list = [];
      if (all) this.fits.forEach(function (f, fi) { list = list.concat(self.colourwaysOf(fi)); });
      else list = this.colourwaysOf(this.packFit);
      list.forEach(function (x, i) {
        var b = self.swatch(x), name = x.c.colour;
        if (all) { var f = self.fits[x.fi] || {}; name = [f.label, self.colourwaysOf(x.fi).length > 1 ? x.c.colour : ''].filter(Boolean).join(' '); b.title = name; }
        b.dataset.lpPick = x.p.handle; b.dataset.i = i;
        b.setAttribute('aria-label', 'Add ' + name); phost.appendChild(b);
      });
      phost.scrollLeft = 0;
      if (tabs) {
        tabs.hidden = this.fits.length < 2 || all;
        if (!tabs.children.length) this.fits.forEach(function (f, i) {
          var t = document.createElement('button');
          t.type = 'button'; t.className = 'lp-video-buy__pack-fit'; t.setAttribute('role', 'tab'); t.dataset.lpPackFit = i; t.textContent = f.label || '';
          tabs.appendChild(t);
        });
      }
    },
    checkNext: function () {
      var el = this.el;
      [['[data-lp-swatches]', '[data-lp-swatch-next=""]'], ['[data-lp-pack-swatches]', '[data-lp-swatch-next="pack"]'], ['[data-lp-fits]', '[data-lp-swatch-next="fits"]']].forEach(function (pair) {
        var host = el.querySelector(pair[0]), next = el.querySelector(pair[1]);
        if (host && next) next.hidden = host.scrollWidth <= host.clientWidth + 2;
      });
    },
    nextColours: function (which) {
      var host = this.el.querySelector(which === 'pack' ? '[data-lp-pack-swatches]' : which === 'fits' ? '[data-lp-fits]' : '[data-lp-swatches]'); if (!host) return;
      var atEnd = host.scrollLeft + host.clientWidth >= host.scrollWidth - 2;
      host.scrollTo({ left: atEnd ? 0 : host.scrollLeft + host.clientWidth * 0.8, behavior: motionOK() ? 'smooth' : 'auto' });
    },
    render: function (change) {
      var self = this, cur = this.current(), el = this.el; if (!cur) return;
      var p = cur.p, c = cur.c, fmt = this.root.format;
      this.settleOpt(cur);
      var opt = this.optOf(cur);
      /* the picked size stays when another colour is previewed (sold out there = "Sold out"), so the colour row, sorted
         by that size, doesn't reshuffle under the tap */
      var v = this.variant(), anyStock = c.variants.some(function (x) { return x.available && self.optOk(cur, x); });
      var price = v ? v.price : Math.min.apply(null, c.variants.map(function (x) { return x.price; }));
      if (this.building) price = this.packAverage(price);
      var compare = v ? v.compare : 0, base = price;
      if (this.root.tiers) { var u = this.unitFor(this.pack, price); if (u.pct > 0) { compare = Math.max(compare, price); price = u.unit; } }
      el.querySelector('[data-lp-title]').textContent = p.title;
      el.querySelector('[data-lp-price]').textContent = money(price, fmt);
      var cmp = el.querySelector('[data-lp-compare]'); cmp.hidden = !(compare > price); cmp.textContent = compare > price ? money(compare, fmt) : '';
      var note = el.querySelector('[data-lp-note]'); if (note) note.textContent = this.fitData().note || '';
      var cn = el.querySelector('[data-lp-colour-name]'); if (cn) cn.textContent = c.colour;
      var fn = el.querySelector('[data-lp-fit-name]'); if (fn) fn.textContent = this.fitData().label || '';
      slice.call(el.querySelectorAll('[data-lp-swatch]')).forEach(function (b) { b.setAttribute('aria-pressed', (b.dataset.lpSwatch + '|' + b.dataset.colour) === cur.key ? 'true' : 'false'); });
      if (this.sortSwatches(el.querySelector('[data-lp-swatches]'), 'data-lp-swatch')) { var on = el.querySelector('[data-lp-swatch][aria-pressed="true"]'); if (on) revealInline(on, false); }
      var ribbon = el.querySelector('[data-lp-ribbon]'); if (ribbon) ribbon.hidden = anyStock;
      /* sizes */
      var sizes = el.querySelector('[data-lp-sizes]'), srow = el.querySelector('[data-lp-size-row]');
      sizes.innerHTML = '';
      /* one pill per size name; with an opt, a size is in stock when its variant in the picked opt is */
      var sized = [], seen = {};
      c.variants.forEach(function (x) {
        if (!x.size || !self.optOk(cur, x)) return;
        var k = shortSize(x.size);
        if (seen[k]) { if (x.available) seen[k].available = true; return; }
        seen[k] = { size: x.size, available: x.available }; sized.push(seen[k]);
      });
      if (srow) srow.hidden = !sized.length;
      sized.forEach(function (x) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'lp-video-buy__size'; b.dataset.lpSize = x.size; b.textContent = shortSize(x.size);
        b.setAttribute('aria-pressed', sameSize(x.size, self.size) ? 'true' : 'false');
        b.setAttribute('aria-label', x.size + (x.available ? '' : ', sold out'));
        if (!x.available) b.setAttribute('data-sold-out', '');
        sizes.appendChild(b);
      });
      /* third option (Absorbency): pills in the product's order; a value with no stock in the picked size is slashed */
      var orow = el.querySelector('[data-lp-opt-row]'), ohost = el.querySelector('[data-lp-opts]');
      if (orow && ohost) {
        orow.hidden = !opt; ohost.innerHTML = '';
        if (opt) {
          var oname = el.querySelector('[data-lp-opt-name]'), oval = el.querySelector('[data-lp-opt-value]');
          var shown = this.optFor(cur);
          if (oname) oname.textContent = opt.name; if (oval) oval.textContent = shown || '';
          opt.values.forEach(function (val) {
            var ok = c.variants.some(function (y) { return y.opt === val && y.available && (!self.size || !y.size || sameSize(y.size, self.size)); });
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'lp-video-buy__size'; b.dataset.lpOpt = val; b.textContent = val;
            b.setAttribute('aria-pressed', val === shown ? 'true' : 'false');
            b.setAttribute('aria-label', val + (ok ? '' : ', sold out'));
            if (!ok) b.setAttribute('data-sold-out', '');
            ohost.appendChild(b);
          });
        }
      }
      /* buy row */
      var q = el.querySelector('[data-lp-qty-value]'); if (q) q.value = this.root.tiers ? this.pack : this.qty;
      var qm = el.querySelector('[data-lp-qty-step="-1"]'); if (qm) qm.disabled = (this.root.tiers ? this.pack : this.qty) <= 1;
      var atc = el.querySelector('[data-lp-atc]'), label = el.querySelector('[data-lp-atc-label]');
      if (this.building) { var sizedAny = this.colourways().some(function (x) { return x.c.variants.some(function (y) { return y.size; }); }); label.textContent = sizedAny && !this.size ? this.root.dataset.sizePrompt : atc.dataset.label; atc.disabled = false; }
      else if (!anyStock) { label.textContent = atc.dataset.soldOut; atc.disabled = true; }
      else if (sized.length && !v) { label.textContent = this.root.dataset.sizePrompt; atc.disabled = false; }
      else if (v && !v.available) { label.textContent = atc.dataset.soldOut; atc.disabled = true; }
      else { label.textContent = atc.dataset.label; atc.disabled = !v; }
      var mt = el.querySelector('[data-lp-mini-title]'), mp = el.querySelector('[data-lp-mini-price]'), mc = el.querySelector('[data-lp-mini-colour]');
      if (mt) mt.textContent = p.title;
      if (mp) mp.textContent = money(price, fmt);
      if (mc) mc.textContent = this.colourways().length > 1 ? c.colour : '';
      var sum = el.querySelector('[data-lp-summary]'), picked = this.slots.map(function (sl) { var x = self.byKey(sl.k); return x ? x.c.colour : ''; }).filter(function (n, i, a) { return n && a.indexOf(n) === i; });
      var fitName = this.fitData().label;
      if (this.building && this.root.fitPhotos && this.slots.length) fitName = this.slots.map(function (sl) { var x = self.byKey(sl.k); return x ? (self.fits[x.fi] || {}).label : ''; }).filter(function (n, i, a) { return n && a.indexOf(n) === i; }).join(' + ');
      if (sum) sum.textContent = [fitName, this.building ? picked.join(' + ') : c.colour, this.size ? shortSize(this.size) : null, opt ? this.optFor(cur) : null].filter(Boolean).join(' · ') + (this.root.tiers && this.pack > 1 ? ' × ' + this.pack : '');
      if (this.root.tiers) this.renderPack(base);
      this.renderAddon(base, anyStock);
      if (this.index === this.root.active) { this.root.tint(c.swatch); if (window.LP && change !== 'instant') window.LP.emit('selection', this.selection()); }
      var tiers = el.querySelector('[data-lp-tiers]');
      if (tiers) tiers.innerHTML = (p.tiers || '').split(',').filter(Boolean).map(function (t) { var kv = t.split(':'); return 'Buy ' + kv[0] + ', <strong>save ' + kv[1] + '%</strong>'; }).join(' · ');
      var err = el.querySelector('[data-lp-error]'); if (err && change !== 'cart') err.hidden = true;
      var chart = this.root.guideChart(this.index + '-' + this.fit), gb = el.querySelector('[data-lp-guide]'), gl = el.querySelector('[data-lp-guide-link]');
      if (gb) gb.hidden = !chart; if (gl) gl.hidden = !!chart;
      this.setGallery(c, change);
      if (change === 'fit' && this.root.gsap && motionOK()) this.root.gsap.fromTo(el.querySelector('[data-lp-form]'), { autoAlpha: 0.4, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.4, ease: 'power2.out', clearProps: 'all' });
      this.placeRing(false);
    },
    /* photo gallery: the colourway's images as a snap track, a progress bar under it, prev / next over its edges */
    setGallery: function (c, change) {
      var el = this.el, track = el.querySelector('[data-lp-track]'); if (!track) return;
      var imgs = (c.images && c.images.length ? c.images : (c.image ? [c.image] : [])), key = imgs.join('|');
      if (track.dataset.key === key) return;
      track.dataset.key = key;
      var clip = track.querySelector('[data-lp-clip]');
      track.innerHTML = '';
      if (clip) track.appendChild(clip);
      imgs.forEach(function (src, i) {
        var im = document.createElement('img');
        im.className = 'lp-video-buy__img'; im.src = src; im.alt = ''; im.width = 900; im.height = 1125; im.loading = i ? 'lazy' : 'eager';
        track.appendChild(im);
      });
      /* a colour change lands on that colour's first photo (static + phone: the clip is slide 0); anything else starts at the clip */
      var toPhoto = change === 'colour' && !!clip && imgs.length > 0;
      this.stepTo = null; clearTimeout(this.stepTimer);
      this.slide = toPhoto ? 1 : 0; track.scrollTo({ left: toPhoto ? track.clientWidth : 0, behavior: 'auto' });
      var count = track.children.length;
      slice.call(el.querySelectorAll('[data-lp-nav]')).forEach(function (b) { b.hidden = count < 2; });
      var bar = el.querySelector('.lp-video-buy__gallery-bar'); if (bar) bar.hidden = count < 2;
      this.gallerySync();
      if (change !== 'instant' && motionOK() && imgs.length) { track.classList.remove('is-in'); void track.offsetWidth; track.classList.add('is-in'); }
    },
    gallerySync: function () {
      var el = this.el, track = el.querySelector('[data-lp-track]'), n = track ? track.children.length : 0, bar = el.querySelector('[data-lp-gallery-bar]');
      if (track && n && track.clientWidth) {
        var at = Math.max(0, Math.min(n - 1, Math.round(track.scrollLeft / track.clientWidth)));
        /* 07/10/2026: while a prev / next smooth scroll is under way the track still reports the slide it is leaving,
           which pulled the bar back before it went forward; hold the target until the scroll reaches it */
        if (this.stepTo != null) { if (at !== this.stepTo) return; this.stepTo = null; clearTimeout(this.stepTimer); }
        this.slide = at;
      }
      if (bar && n) { bar.style.width = (100 / n) + '%'; bar.style.transform = 'translateX(' + (this.slide * 100) + '%)'; }
      /* static + phone: the clip is slide 0 — its controls hide and it pauses once a photo is in view */
      var photo = el.querySelector('[data-lp-photo]'), clip = track && track.querySelector('[data-lp-clip]');
      if (photo) { var onPhoto = !!clip && this.slide > 0; photo.classList.toggle('is-on-photo', onPhoto); if (onPhoto) { var cv = clip.querySelector('video'); if (cv && !cv.paused) cv.pause(); } else if (clip && this.root.static) { this.root.playClips(); } }
    },
    galleryStep: function (dir) {
      var track = this.el.querySelector('[data-lp-track]'); if (!track || track.children.length < 2) return;
      var n = track.children.length, i = (this.slide + dir + n) % n, self = this;
      this.slide = i; this.stepTo = i;
      clearTimeout(this.stepTimer); this.stepTimer = setTimeout(function () { self.stepTo = null; self.gallerySync(); }, 1000);
      track.scrollTo({ left: i * track.clientWidth, behavior: motionOK() ? 'smooth' : 'auto' });
      var bar = this.el.querySelector('[data-lp-gallery-bar]'); if (bar) bar.style.transform = 'translateX(' + (i * 100) + '%)';
    },
    placeThumb: function (animate) {
      var thumb = this.el.querySelector('[data-lp-thumb]'), on = this.el.querySelector('[data-lp-fit].is-active');
      if (!thumb || !on) return; /* the fit toggle is plain pills now; kept for a track with a thumb */
      var x = on.offsetLeft, w = on.offsetWidth;
      if (animate && this.root.gsap && motionOK()) this.root.gsap.to(thumb, { x: x, width: w, duration: 0.45, ease: 'expo.out' });
      else { thumb.style.transform = 'translateX(' + x + 'px)'; thumb.style.width = w + 'px'; }
    },
    placeRing: function (animate, btn) {
      var ring = this.el.querySelector('[data-lp-ring]'); if (!ring) return;
      var on = btn || this.el.querySelector('[data-lp-swatch][aria-pressed="true"]');
      if (!on) { ring.classList.remove('is-on'); return; }
      var x = on.offsetLeft, y = on.offsetTop, was = ring.classList.contains('is-on'), g = this.root.gsap;
      ring.classList.add('is-on');
      if (animate && was && g && motionOK()) g.to(ring, { x: x, y: y, duration: 0.4, ease: 'expo.out' });
      else if (g) g.set(ring, { x: x, y: y }); else ring.style.transform = 'translate(' + x + 'px,' + y + 'px)';
    },
    add: function () {
      var self = this, el = this.el, v = this.variant(), cur = this.current(), btn = el.querySelector('[data-lp-atc]'), err = el.querySelector('[data-lp-error]');
      if (btn.getAttribute('aria-busy') === 'true') return;
      var needsSize = this.colourways().some(function (x) { return x.c.variants.some(function (y) { return y.size; }); });
      if (this.building) {
        if (needsSize && !this.size && !this.perPair()) { this.shake('[data-lp-sizes]'); return; }
        if (this.slots.length < this.pack) { this.shake('[data-lp-slots]'); return; }
        var bad = this.slots.filter(function (sl) { return !self.slotOk(sl); })[0];
        if (bad) { var bx = this.byKey(bad.k); err.hidden = false; err.textContent = (bx ? bx.c.colour : 'A colour') + ' is sold out in ' + [shortSize(this.slotSize(bad)), bad.o && this.optOf(bx) ? bad.o : ''].filter(Boolean).join(' ') + '.'; this.shake('[data-lp-slots]'); return; }
      } else {
        if (!v) { this.shake('[data-lp-sizes]'); return; }
        if (!v.available) return;
      }
      var items = this.items(); if (!items.length) return;
      var firstId = items[0].id;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      btn.setAttribute('aria-busy', 'true');
      fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: items, sections: sections.join(','), sections_url: root + 'variants/' + firstId }) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          btn.removeAttribute('aria-busy');
          if (!res.ok) { err.hidden = false; err.textContent = (res.json && res.json.description) || 'Could not add to cart. Please try again.'; return; }
          return fetch(root + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            cart.sections = res.json.sections;
            if (!cart.sections) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            self.root.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: res.json.items || [res.json], cart: cart } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
            if (window.LP) window.LP.emit('cart-add', { items: items, cart: cart, source: 'lp-video-buy', handle: cur.p.handle });
            self.root.closeSheet();
          });
        })
        .catch(function () { btn.removeAttribute('aria-busy'); err.hidden = false; err.textContent = 'Could not add to cart. Please try again.'; });
    }
  };

  /* ---------------- particles: one canvas per band ---------------- */
  function Motif(root, el) {
    this.root = root; this.el = el; this.canvas = el.querySelector('[data-lp-motif-canvas]');
    this.ctx = this.canvas ? this.canvas.getContext('2d', { alpha: true }) : null;
    this.kind = ''; this.dots = []; this.raf = 0; this.w = 0; this.h = 0; this.t = 0; this.last = 0;
    var self = this;
    this.ro = 'ResizeObserver' in window ? new ResizeObserver(function () { self.size(); }) : null;
    if (this.ro) this.ro.observe(el);
    this.size();
  }
  Motif.prototype = {
    /* dots per 100k px²; the rest of each recipe is in draw() */
    recipes: {
      embers:  { density: 16, make: function (w, h) { return { x: Math.random() * w, y: Math.random() * h, r: .8 + Math.random() * 1.6, vy: -(14 + Math.random() * 22), sway: 8 + Math.random() * 14, ph: Math.random() * 6.28, fl: 1.2 + Math.random() * 2.2, a: .35 + Math.random() * .55 }; } },
      bubbles: { density: 7, make: function (w, h) { return { x: Math.random() * w, y: Math.random() * h, r: 1.5 + Math.random() * 5.5, vy: -(10 + Math.random() * 16), sway: 4 + Math.random() * 8, ph: Math.random() * 6.28, a: .25 + Math.random() * .4, ring: Math.random() > .35 }; } },
      /* wind: three kinds in one list — streaks (long, fading at both ends, faster when thinner), motes (tiny specks
         riding the same wind) and mist (a few wide slow banks); `k` picks the kind */
      wisps:   { density: 9, make: function (w, h) {
        var u = Math.random();
        if (u < .12) return { k: 'mist', x: Math.random() * w, y: Math.random() * h, len: 260 + Math.random() * 420, r: 60 + Math.random() * 90, vx: 6 + Math.random() * 10, sway: 6 + Math.random() * 10, ph: Math.random() * 6.28, a: .10 + Math.random() * .10 };
        if (u < .5) { var th = .6 + Math.random() * 2.4; return { k: 'streak', x: Math.random() * w, y: Math.random() * h, len: 70 + Math.random() * 300, r: th, vx: (16 + Math.random() * 40) * (3.4 - th) / 2, sway: 2 + Math.random() * 7, ph: Math.random() * 6.28, a: .16 + Math.random() * .3 }; }
        return { k: 'mote', x: Math.random() * w, y: Math.random() * h, r: .6 + Math.random() * 1.2, vx: 10 + Math.random() * 26, sway: 6 + Math.random() * 12, ph: Math.random() * 6.28, a: .25 + Math.random() * .4 };
      } },
      leaves:  { density: 8, make: function (w, h) { return { x: Math.random() * w, y: Math.random() * h, rx: 1.4 + Math.random() * 2.2, ry: .9 + Math.random() * 1.4, vy: 9 + Math.random() * 14, sway: 10 + Math.random() * 18, ph: Math.random() * 6.28, rot: Math.random() * 6.28, vr: (Math.random() - .5) * 1.6, a: .3 + Math.random() * .4 }; } }
    },
    size: function () {
      if (!this.ctx) return;
      var r = this.el.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var w = Math.round(r.width), h = Math.round(r.height); if (!w || !h) return;
      if (w === this.w && h === this.h) return;
      var widthChanged = w !== this.w;
      this.half = w < 768;
      this.w = w; this.h = h; this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      /* a height change (the socks drawer opening) keeps the dots where they are; only a new width reseeds */
      if (widthChanged || !this.dots.length) this.seed(true);
    },
    setKind: function (kind) { if (kind === this.kind) return; this.kind = kind; this.seed(false); },
    seed: function (keepCount) {
      var rec = this.recipes[this.kind]; if (!rec || !this.w) { this.dots = []; return; }
      var n = Math.min(420, Math.round(this.w * this.h / 1e5 * rec.density));
      var self = this; this.dots = []; for (var i = 0; i < n; i++) this.dots.push(rec.make(self.w, self.h));
      this.colours();
    },
    colours: function () { var cs = getComputedStyle(this.el); this.dot = cs.color; this.glow = cs.borderTopColor; },
    /* alpha thins behind the copy (the old mask): 1 at the edges, ~.25 in the middle */
    fade: function (x, y) { var dx = (x - this.w * .5) / (this.w * .5), dy = (y - this.h * .45) / (this.h * .5); var d = Math.sqrt(dx * dx + dy * dy); return Math.max(.22, Math.min(1, (d - .25) / .6)); },
    frame: function (now) {
      var self = this; this.raf = 0;
      if (!this.running) return;
      /* phones: every other frame (30 fps) — plenty for drifting dots, half the battery */
      if (this.half && this.last && now - this.last < 28) { this.raf = requestAnimationFrame(function (n) { self.frame(n); }); return; }
      var dt = this.last ? Math.min(.05, (now - this.last) / 1000) : .016; this.last = now; this.t += dt;
      var ctx = this.ctx, w = this.w, h = this.h, t = this.t; if (!ctx || !w) { this.raf = requestAnimationFrame(function (n) { self.frame(n); }); return; }
      ctx.clearRect(0, 0, w, h);
      var k = this.kind;
      if (k === 'embers') {
        /* heat glow breathing along the bottom edge */
        var g = ctx.createRadialGradient(w * .3, h, 0, w * .3, h, h * .55 * (1 + .06 * Math.sin(t * 1.4)));
        g.addColorStop(0, this.glow); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalAlpha = .34; ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
        g = ctx.createRadialGradient(w * .78, h, 0, w * .78, h, h * .45 * (1 + .06 * Math.cos(t * 1.1)));
        g.addColorStop(0, this.glow); g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
        ctx.fillStyle = this.dot;
        this.dots.forEach(function (d) {
          d.y += d.vy * dt; d.x += Math.sin(t * .9 + d.ph) * d.sway * dt;
          if (d.y < -6) { d.y = h + 6; d.x = Math.random() * w; }
          ctx.globalAlpha = d.a * (.55 + .45 * Math.sin(t * d.fl + d.ph)) * self.fade(d.x, d.y);
          ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, 6.2832); ctx.fill();
        });
      } else if (k === 'bubbles') {
        ctx.strokeStyle = this.dot; ctx.fillStyle = this.dot; ctx.lineWidth = 1;
        this.dots.forEach(function (d) {
          d.y += d.vy * dt; d.x += Math.sin(t * .7 + d.ph) * d.sway * dt;
          if (d.y < -8) { d.y = h + 8; d.x = Math.random() * w; }
          ctx.globalAlpha = d.a * self.fade(d.x, d.y);
          ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, 6.2832);
          if (d.ring) { ctx.stroke(); ctx.beginPath(); ctx.arc(d.x + d.r * .4, d.y - d.r * .4, Math.max(.6, d.r * .22), 0, 6.2832); ctx.fill(); } else ctx.fill();
        });
      } else if (k === 'wisps') {
        ctx.lineCap = 'round'; ctx.fillStyle = this.dot;
        var clear = 'rgba(0,0,0,0)';
        this.dots.forEach(function (d) {
          d.x += d.vx * dt;
          var yy = d.y + Math.sin(t * .6 + d.ph) * d.sway;
          if (d.k === 'mist') {
            if (d.x - d.len > w) { d.x = -d.len; d.y = Math.random() * h; }
            var mg = ctx.createRadialGradient(d.x - d.len * .5, yy, 0, d.x - d.len * .5, yy, d.len * .5);
            mg.addColorStop(0, self.glow); mg.addColorStop(1, clear);
            ctx.globalAlpha = d.a; ctx.fillStyle = mg;
            ctx.save(); ctx.translate(d.x - d.len * .5, yy); ctx.scale(1, d.r / (d.len * .5)); ctx.beginPath(); ctx.arc(0, 0, d.len * .5, 0, 6.2832); ctx.fill(); ctx.restore();
            ctx.fillStyle = self.dot;
          } else if (d.k === 'streak') {
            if (d.x - d.len > w) { d.x = -d.len; d.y = Math.random() * h; }
            var x0 = d.x - d.len, sg = ctx.createLinearGradient(x0, 0, d.x, 0);
            sg.addColorStop(0, clear); sg.addColorStop(.35, self.dot); sg.addColorStop(.7, self.dot); sg.addColorStop(1, clear);
            ctx.globalAlpha = d.a * self.fade(d.x - d.len * .5, yy); ctx.lineWidth = d.r; ctx.strokeStyle = sg;
            ctx.beginPath(); ctx.moveTo(x0, yy + Math.sin(t + d.ph) * 3); ctx.quadraticCurveTo(d.x - d.len * .5, yy - 4, d.x, yy); ctx.stroke();
          } else {
            if (d.x > w + 4) { d.x = -4; d.y = Math.random() * h; }
            ctx.globalAlpha = d.a * self.fade(d.x, yy);
            ctx.beginPath(); ctx.arc(d.x, yy, d.r, 0, 6.2832); ctx.fill();
          }
        });
      } else if (k === 'leaves') {
        ctx.fillStyle = this.dot;
        this.dots.forEach(function (d) {
          d.y += d.vy * dt; d.x += Math.sin(t * .8 + d.ph) * d.sway * dt; d.rot += d.vr * dt;
          if (d.y > h + 6) { d.y = -6; d.x = Math.random() * w; }
          ctx.globalAlpha = d.a * self.fade(d.x, d.y);
          ctx.save(); ctx.translate(d.x, d.y); ctx.rotate(d.rot); ctx.beginPath(); ctx.ellipse(0, 0, d.rx, d.ry, 0, 0, 6.2832); ctx.fill(); ctx.restore();
        });
      }
      ctx.globalAlpha = 1;
      this.raf = requestAnimationFrame(function (n) { self.frame(n); });
    },
    start: function () {
      if (this.running || !this.ctx || !this.recipes[this.kind]) return;
      if (!motionOK() || document.hidden) return;
      this.running = true; this.last = 0; this.colours(); var self = this; this.raf = requestAnimationFrame(function (n) { self.frame(n); });
    },
    stop: function () { this.running = false; if (this.raf) cancelAnimationFrame(this.raf); this.raf = 0; },
    destroy: function () { this.stop(); if (this.ro) this.ro.disconnect(); }
  };

  /* ---------------- the section ---------------- */
  class LpVideoBuy extends HTMLElement {
    connectedCallback() {
      var self = this, data = [];
      try { data = JSON.parse(this.querySelector('[data-lp-island]').textContent); } catch (e) { data = []; }
      this.format = this.dataset.moneyFormat || '';
      this.gsap = null; this.wantSound = false; this.stageInView = false; this.active = 0;
      this.desktop = window.matchMedia('(min-width: 48rem)');
      this.static = this.classList.contains('lp-video-buy--static');
      this.viewer = this.querySelector('[data-lp-viewer]'); this.full = this.querySelector('[data-lp-full]');
      this.guide = this.querySelector('[data-lp-guide-dialog]');
      this.zoomEl = this.querySelector('[data-lp-zoom]');
      this.tiers = (this.dataset.tiers || '').split(',').map(function (n) { return parseInt(n, 10); }).filter(function (n) { return n > 0; });
      if (!this.tiers.length) this.tiers = null;
      this.valueTiers = (this.dataset.valueTiers || '').split(',').map(function (t) { var kv = t.split(':'); return { at: Math.round(parseFloat(kv[0]) * 100), pct: parseFloat(kv[1]) }; }).filter(function (t) { return t.at >= 0 && t.pct > 0; });
      this.cart = parseInt(this.dataset.cart, 10) || 0;
      this.tintEl = this.querySelector('.lp-video-buy__tint');
      var motifEl = this.querySelector('.lp-video-buy__motif');
      this.motif = motifEl ? new Motif(this, motifEl) : null;
      if (this.motif) {
        this.motif.setKind(motifEl.dataset.lpMotif || 'none');
        this.onVis = function () { if (document.hidden) self.motif.stop(); else if (self.classList.contains('is-on')) self.motif.start(); };
        document.addEventListener('visibilitychange', this.onVis);
      }
      var td = parseInt(this.dataset.tierDefault, 10);
      this.tierDefault = this.tiers ? (this.tiers.indexOf(td) > -1 ? td : this.tiers[0]) : 1;
      this.fitPhotos = this.classList.contains('lp-video-buy--fits-photos');
      this.families = slice.call(this.querySelectorAll('[data-lp-family]'));
      this.panels = this.families.map(function (f, i) { return new Panel(self, f, data[i] || {}); });
      /* packs and the socks add-on price against the live cart: follow every cart change (ours included) */
      if (this.tiers || this.querySelector('[data-lp-addon]')) {
        this.onCart = function (e) {
          var c = e && e.detail && e.detail.cart;
          var apply = function (cart) { if (!cart || cart.original_total_price == null) return; self.cart = cart.original_total_price; self.panels.forEach(function (p) { p.render('cart'); }); };
          if (c && c.original_total_price != null) apply(c);
          else fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js').then(function (r) { return r.json(); }).then(apply).catch(function () {});
        };
        document.documentElement.addEventListener('cart:change', this.onCart);
      }
      this.video = this.querySelector('[data-lp-video]');
      this.veil = this.querySelector('[data-lp-veil]');

      this.onClick = function (e) {
        if (self.viewer) {
          if (e.target.closest('[data-lp-close]')) { self.closeViewer(); return; }
          if (e.target.closest('[data-lp-mute]')) { self.full.muted = !self.full.muted; self.querySelector('[data-lp-mute]').setAttribute('aria-pressed', self.full.muted ? 'true' : 'false'); return; }
          if (e.target.closest('[data-lp-toggle-play]')) { if (self.full.paused) self.full.play().catch(function () {}); else self.full.pause(); return; }
          if (e.target === self.viewer) { self.closeViewer(); return; }
        }
        if (self.guide && e.target === self.guide) { self.guide.close(); return; } /* backdrop */
        if (e.target.matches && e.target.matches('[data-lp-sock-dialog]')) { e.target.close(); return; }
        if (self.zoomEl && self.zoomEl.contains(e.target)) {
          if (e.target.closest('[data-lp-zoom-close]')) { self.closeZoom(); return; }
          var zs = e.target.closest('[data-lp-zoom-step]'); if (zs) { self.zoomShow(self.zi + (+zs.dataset.lpZoomStep)); return; }
          if (e.target === self.zoomEl) { self.closeZoom(); return; }
          return;
        }
        var t, fam = e.target.closest('[data-lp-family]'), panel = fam ? self.panels[self.families.indexOf(fam)] : null;
        if (panel) {
          if ((t = e.target.closest('[data-lp-fit]'))) { panel.setFit(parseInt(t.dataset.lpFit, 10)); return; }
          if ((t = e.target.closest('[data-lp-swatch]'))) { panel.setColour(t.dataset.lpSwatch, t.dataset.colour, t); revealInline(t, motionOK()); return; }
          if ((t = e.target.closest('[data-lp-pick]'))) { panel.pick(t.dataset.lpPick, t.dataset.colour); revealInline(t, motionOK()); var ms = fam.querySelector('[data-lp-swatch][aria-pressed="true"]'); if (ms) revealInline(ms, motionOK()); return; }
          if ((t = e.target.closest('[data-lp-tier]'))) { panel.setPack(parseInt(t.dataset.lpTier, 10)); return; }
          if (e.target.closest('[data-lp-build]')) { panel.toggleBuild(); return; }
          if ((t = e.target.closest('[data-lp-slot]'))) { panel.unslot(parseInt(t.dataset.lpSlot, 10)); return; }
          if ((t = e.target.closest('[data-lp-pack-fit]'))) { panel.setPackFit(parseInt(t.dataset.lpPackFit, 10)); return; }
          if (e.target.closest('[data-lp-guide]')) { self.openGuide(panel); return; }
          if ((t = e.target.closest('[data-lp-swatch-next]'))) { panel.nextColours(t.dataset.lpSwatchNext); return; }
          if ((t = e.target.closest('[data-lp-nav]'))) { panel.galleryStep(parseInt(t.dataset.lpNav, 10)); return; }
          if ((t = e.target.closest('.lp-video-buy__img')) && self.zoomEl) { self.openZoom(panel, slice.call(t.parentElement.querySelectorAll('.lp-video-buy__img')).indexOf(t)); return; }
          if ((t = e.target.closest('[data-lp-addon-toggle]'))) { if (t.disabled) return; panel.addon.on = !panel.addon.on; panel.render('addon'); return; }
          if ((t = e.target.closest('[data-lp-addon-style]'))) { panel.addon.style = parseInt(t.dataset.lpAddonStyle, 10); panel.settleSock(); panel.render('addon'); return; }
          if ((t = e.target.closest('[data-lp-addon-colour]'))) { if (t.disabled) return; panel.addon.colour = t.dataset.lpAddonColour; panel.settleSock(); panel.render('addon'); return; }
          if ((t = e.target.closest('[data-lp-addon-size]'))) { if (t.disabled) return; panel.addon.size = t.dataset.lpAddonSize; panel.render('addon'); return; }
          if ((t = e.target.closest('[data-lp-addon-pack]'))) { panel.addon.pack = parseInt(t.dataset.lpAddonPack, 10); panel.render('addon'); return; }
          if (e.target.closest('[data-lp-addon-guide]')) { self.openSockGuide(panel); return; }
          if ((t = e.target.closest('[data-lp-size]'))) { panel.size = t.dataset.lpSize; panel.render(); return; }
          if ((t = e.target.closest('[data-lp-opt]'))) { panel.opt = t.dataset.lpOpt; panel.render(); return; }
          if ((t = e.target.closest('[data-lp-qty-step]'))) {
            if (self.tiers) panel.setPack(Math.max(1, Math.min(99, panel.pack + parseInt(t.dataset.lpQtyStep, 10))));
            else { panel.qty = Math.max(1, Math.min(10, panel.qty + parseInt(t.dataset.lpQtyStep, 10))); panel.render(); }
            return;
          }
          if (e.target.closest('[data-lp-atc]')) { panel.add(); return; }
          if (e.target.closest('[data-lp-open-sheet]')) { self.openSheet(fam); return; }
          if (e.target.closest('[data-lp-close-sheet]')) { self.closeSheet(); return; }
          if (e.target.matches && e.target.matches('[data-lp-form]') && fam === self.sheetFam) { self.closeSheet(); return; } /* backdrop */
          if (e.target.closest('[data-lp-clip-sound]')) { self.toggleClipSound(fam); return; }
          if (e.target.closest('[data-lp-clip-expand]')) { self.openViewer(fam); return; }
          if (e.target.closest('[data-lp-clip-toggle]') || (self.static && e.target.closest('[data-lp-clip]'))) { self.toggleClipPlay(fam); return; }
          if (e.target.closest('[data-lp-clip]')) { self.toggleClipSound(fam); return; }
        }
        if (e.target.closest('[data-lp-sound]')) { self.setSound(!self.wantSound); return; }
      };
      this.addEventListener('click', this.onClick);
      /* a link elsewhere on the page names a product (lp-hero card → lp:product-change): show the fit that sells it */
      if (window.LP && window.LP.on) this.offProduct = window.LP.on('product-change', function (e) {
        var h = e.detail && e.detail.handle; if (!h) return;
        self.panels.some(function (p) {
          return p.fits.some(function (f, fi) {
            if (!f.products.some(function (pr) { return pr.handle === h; })) return false;
            if (fi !== p.fit) p.setFit(fi);
            var x = p.colourways().filter(function (y) { return y.p.handle === h; })[0];
            if (x && x.key !== p.cw) p.setColour(x.p.handle, x.c.colour);
            var on = p.el.querySelector('[data-lp-fits] [data-lp-fit].is-active'); if (on) revealInline(on, false);
            return true;
          });
        });
      });
      this.onBlockSelect = function (e) {
        var fam = e.target && e.target.closest ? e.target.closest('[data-lp-family]') : null;
        if (fam && self.contains(fam)) { self.setActive(self.families.indexOf(fam)); }
      };
      this.onKey = function (e) { if (e.key === 'Escape' && self.sheetFam) self.closeSheet(); };
      document.addEventListener('keydown', this.onKey);
      this.onFormClose = function (e) { var fam = e.target.closest('[data-lp-family]'); if (fam && fam === self.sheetFam) self.closeSheet(); };
      this.families.forEach(function (f) { var form = f.querySelector('[data-lp-form]'); if (form) form.addEventListener('close', self.onFormClose); });
      if (this.viewer && this.full) {
        this.onVTime = function () { var bar = self.querySelector('[data-lp-progress]'), seek = self.querySelector('[data-lp-seek]'); if (bar && self.full.duration) { var pc = self.full.currentTime / self.full.duration * 100; bar.style.width = pc + '%'; if (seek) seek.setAttribute('aria-valuenow', Math.round(pc)); } };
        /* scrub: press or drag on the bar seeks; arrow keys step 5s */
        var seekEl = this.querySelector('[data-lp-seek]');
        if (seekEl) {
          var seekTo = function (e) { var r = seekEl.getBoundingClientRect(); if (!r.width || !self.full.duration) return; var x = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)); self.full.currentTime = x * self.full.duration; self.onVTime(); };
          this.onSeekDown = function (e) { e.preventDefault(); try { seekEl.setPointerCapture(e.pointerId); } catch (x) {} self.scrubbing = true; seekTo(e); };
          this.onSeekMove = function (e) { if (self.scrubbing) seekTo(e); };
          this.onSeekUp = function () { self.scrubbing = false; };
          this.onSeekKey = function (e) { if (!self.full.duration) return; if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); self.full.currentTime = Math.max(0, Math.min(self.full.duration, self.full.currentTime + (e.key === 'ArrowRight' ? 5 : -5))); self.onVTime(); } };
          seekEl.addEventListener('pointerdown', this.onSeekDown); seekEl.addEventListener('pointermove', this.onSeekMove); seekEl.addEventListener('pointerup', this.onSeekUp); seekEl.addEventListener('pointercancel', this.onSeekUp); seekEl.addEventListener('keydown', this.onSeekKey);
        }
        this.onVState = function () { self.viewer.classList.toggle('is-paused', self.full.paused); };
        this.full.addEventListener('timeupdate', this.onVTime); this.full.addEventListener('play', this.onVState); this.full.addEventListener('pause', this.onVState);
        this.onViewerClose = function () { self.full.pause(); self.full.removeAttribute('src'); self.full.load(); document.body.style.overflow = ''; self.playClips(); };
        this.viewer.addEventListener('close', this.onViewerClose);
      }
      this.families.forEach(function (f) { var v = f.querySelector('.lp-video-buy__clip-video'); if (v) { v.addEventListener('play', function () { self.syncClip(f); }); v.addEventListener('pause', function () { self.syncClip(f); }); v.addEventListener('volumechange', function () { self.syncClip(f); }); } });
      if (this.zoomEl) this.initZoom();
      this.placeClips();
      this.syncForms();
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.onResize = function () { self.placeClips(); self.syncForms(); self.panels.forEach(function (p) { p.placeThumb(false); p.placeRing(false); p.checkNext(); }); self.playClips(); self.track(); };
      window.addEventListener('resize', this.onResize);
      if (this.video) { this.onTime = function () { var bar = self.querySelector('[data-lp-bar]'); if (bar && self.video.duration) bar.style.width = (self.video.currentTime / self.video.duration * 100) + '%'; }; this.video.addEventListener('timeupdate', this.onTime); }

      /* GSAP + first clip when the section nears the screen */
      var f0 = this.families[0];
      if (this.video && f0 && f0.dataset.poster) this.video.poster = f0.dataset.poster;
      if (window.LP && window.LP.near) {
        this.offNear = window.LP.near(this, function () {
          if (window.LP.gsap && self.dataset.lpGsap) window.LP.gsap([self.dataset.lpGsap]).then(function (g) { self.gsap = g; });
          self.loadStage(0);
        });
      } else { this.loadStage(0); }
      this.watch();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.offProduct) this.offProduct();
      if (this.motif) this.motif.destroy(); if (this.onVis) document.removeEventListener('visibilitychange', this.onVis);
      if (this.onCart) document.documentElement.removeEventListener('cart:change', this.onCart);
      if (this.onZoomKey) document.removeEventListener('keydown', this.onZoomKey);
      this.closeZoom();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('keydown', this.onKey);
      window.removeEventListener('resize', this.onResize);
      this.closeSheet();
      if (this.familyIO) this.familyIO.disconnect();
      if (this.onIO) this.onIO.disconnect(); if (this.stageIO) this.stageIO.disconnect();
      window.removeEventListener('scroll', this.onScroll); this.removeEventListener('scroll', this.onTrackScroll, true); if (this.raf) cancelAnimationFrame(this.raf);
      if (this.pageTween) this.pageTween.kill();
      window.removeEventListener('wheel', this.onWheelBound); window.removeEventListener('keydown', this.onArrowBound);
      if (this.stageIO) this.stageIO.disconnect();
      if (this.viewer) { this.viewer.removeEventListener('close', this.onViewerClose); this.closeViewer(); }
      if (this.offNear) this.offNear();
      if (this.tl) this.tl.kill();
      if (this.video) this.video.pause();
      this.families.forEach(function (f) { var v = f.querySelector('video'); if (v) v.pause(); });
    }

    /* ---------- photo zoom (the lp-bundle-buy viewer): the zoomed panel's current colour photos at 1800px ---------- */
    initZoom() {
      var self = this, zm = this.zoomEl, vp = zm.querySelector('[data-lp-zoom-viewport]'), img = zm.querySelector('[data-lp-zoom-img]');
      if (!vp || !img) return;
      this.zImg = img; this.zi = 0;
      var MIN = 1, MAX = 4, DBL = 2.5, z = { scale: 1, x: 0, y: 0 };
      var start = null, lastTap = 0, swipeX = null, drag = null;
      var clamp = function () {
        var r = vp.getBoundingClientRect(), mx = r.width * (z.scale - 1) / 2, my = r.height * (z.scale - 1) / 2;
        z.x = Math.max(-mx, Math.min(mx, z.x)); z.y = Math.max(-my, Math.min(my, z.y));
      };
      this.zApply = function (anim) {
        img.style.transition = anim && motionOK() ? 'transform .25s ease' : 'none';
        img.style.transform = z.scale === 1 ? '' : 'translate(' + z.x + 'px, ' + z.y + 'px) scale(' + z.scale + ')';
        zm.classList.toggle('is-zoomed', z.scale > 1);
      };
      this.zReset = function () { z.scale = 1; z.x = 0; z.y = 0; self.zApply(false); };
      /* zoom about a viewport point so the pixel under the fingers stays put */
      var zoomAt = function (next, px, py) {
        var r = vp.getBoundingClientRect(), cx = px - r.left - r.width / 2, cy = py - r.top - r.height / 2, k = next / z.scale;
        z.x = cx - (cx - z.x) * k; z.y = cy - (cy - z.y) * k; z.scale = next; clamp();
      };
      var dist = function (t) { return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY); };
      var mid = function (t) { return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 }; };
      vp.addEventListener('touchstart', function (e) {
        var t = e.touches;
        if (t.length === 2) { start = { d: dist(t), scale: z.scale, m: mid(t) }; swipeX = null; }
        else if (t.length === 1) { start = { one: true, px: t[0].clientX, py: t[0].clientY, x: z.x, y: z.y }; swipeX = z.scale === 1 ? t[0].clientX : null; }
      }, { passive: true });
      vp.addEventListener('touchmove', function (e) {
        var t = e.touches; if (!start) return;
        if (t.length === 2 && start.d) {
          e.preventDefault();
          var m = mid(t), next = Math.max(MIN, Math.min(MAX, start.scale * (dist(t) / start.d)));
          zoomAt(next, start.m.x, start.m.y);
          z.x += m.x - start.m.x; z.y += m.y - start.m.y;
          start.m = m; start.scale = next; start.d = dist(t);
          clamp(); self.zApply(false);
        } else if (t.length === 1 && start.one && z.scale > 1) {
          e.preventDefault();
          z.x = start.x + (t[0].clientX - start.px); z.y = start.y + (t[0].clientY - start.py);
          clamp(); self.zApply(false);
        }
      }, { passive: false });
      vp.addEventListener('touchend', function (e) {
        if (e.touches.length) { start = null; return; }
        var ch = e.changedTouches[0];
        if (z.scale <= MIN + 0.05) { z.scale = 1; z.x = 0; z.y = 0; self.zApply(true); }
        if (start && start.one && ch) {
          var moved = Math.hypot(ch.clientX - start.px, ch.clientY - start.py), now = Date.now();
          if (moved < 12) {
            if (now - lastTap < 300) {
              if (z.scale > 1) { z.scale = 1; z.x = 0; z.y = 0; } else zoomAt(DBL, ch.clientX, ch.clientY);
              self.zApply(true); lastTap = 0;
            } else lastTap = now;
          } else if (swipeX !== null && z.scale === 1) {
            var dx = ch.clientX - swipeX;
            if (dx < -50) self.zoomShow(self.zi + 1); else if (dx > 50) self.zoomShow(self.zi - 1);
          }
        }
        start = null; swipeX = null;
      });
      /* mouse: double-click toggles 2.5x at the point, drag pans while zoomed */
      vp.addEventListener('dblclick', function (e) { if (z.scale > 1) { z.scale = 1; z.x = 0; z.y = 0; } else zoomAt(DBL, e.clientX, e.clientY); self.zApply(true); });
      vp.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse' || z.scale === 1) return; drag = { px: e.clientX, py: e.clientY, x: z.x, y: z.y }; try { vp.setPointerCapture(e.pointerId); } catch (x) {} });
      vp.addEventListener('pointermove', function (e) { if (!drag) return; z.x = drag.x + (e.clientX - drag.px); z.y = drag.y + (e.clientY - drag.py); clamp(); self.zApply(false); });
      var endDrag = function () { drag = null; };
      vp.addEventListener('pointerup', endDrag); vp.addEventListener('pointercancel', endDrag);
      /* after closing (button, backdrop or Escape): unlock the page, leave the gallery on the photo last viewed.
         Run directly by closeZoom and again by the close event; the second run finds nothing to do. */
      this.afterZoom = function () {
        if (!self.zoomOpen) return;
        self.zoomOpen = false;
        document.body.style.overflow = '';
        self.zReset();
        var p = self.zPanel, t = p && p.el.querySelector('[data-lp-track]'); if (!t) return;
        var i = (t.querySelector('[data-lp-clip]') ? 1 : 0) + self.zi;
        if (t.children[i]) { p.slide = i; p.stepTo = null; clearTimeout(p.stepTimer); t.scrollTo({ left: i * t.clientWidth, behavior: 'auto' }); p.gallerySync(); }
      };
      zm.addEventListener('close', this.afterZoom);
      this.onZoomKey = function (e) { if (!zm.open) return; if (e.key === 'ArrowRight') self.zoomShow(self.zi + 1); else if (e.key === 'ArrowLeft') self.zoomShow(self.zi - 1); };
      document.addEventListener('keydown', this.onZoomKey);
    }
    zoomList() {
      var cur = this.zPanel && this.zPanel.current(); if (!cur) return [];
      var imgs = cur.c.images && cur.c.images.length ? cur.c.images : (cur.c.image ? [cur.c.image] : []);
      return imgs.map(function (u) { return /width=\d+/.test(u) ? u.replace(/width=\d+/, 'width=1800') : u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=1800'; });
    }
    openZoom(panel, i) {
      var zm = this.zoomEl; if (!zm || !zm.showModal || i < 0) return;
      this.zPanel = panel;
      this.zoomShow(i);
      document.body.style.overflow = 'hidden';
      zm.showModal();
      this.zoomOpen = true;
    }
    zoomShow(i) {
      var list = this.zoomList(), n = list.length, zm = this.zoomEl; if (!n || !zm) return;
      i = (i + n) % n; this.zi = i;
      if (this.zReset) this.zReset();
      var cur = this.zPanel.current();
      this.zImg.src = list[i]; this.zImg.alt = cur.p.title + ' ' + (i + 1) + ' of ' + n;
      var c = zm.querySelector('[data-lp-zoom-current]'), tot = zm.querySelector('[data-lp-zoom-total]');
      if (c) c.textContent = i + 1; if (tot) tot.textContent = n;
      slice.call(zm.querySelectorAll('[data-lp-zoom-step]')).forEach(function (b) { b.hidden = n < 2; });
    }
    closeZoom() { if (this.zoomEl && this.zoomEl.open) this.zoomEl.close(); if (this.afterZoom) this.afterZoom(); }

    /* the sock size guide (one dialog per section), the picked sock size's row marked */
    openSockGuide(panel) {
      var d = this.querySelector('[data-lp-sock-dialog]'); if (!d || !d.showModal) return;
      var sk = panel.sock(), picked = panel.addon.size ? shortSize(panel.addon.size) : '';
      var h = d.querySelector('[data-lp-sock-title]'); if (h && sk) h.textContent = sk.title;
      slice.call(d.querySelectorAll('tr[data-size]')).forEach(function (r) { r.classList.toggle('is-current', !!picked && r.dataset.size === picked); });
      d.showModal();
    }
    /* colour tint: the swatch hex of the colour on show in the active family; anything but a hex clears it */
    tint(swatch) {
      if (!this.tintEl) return;
      var hex = !!(swatch && swatch.charAt(0) === '#'), on = hex && window.LP && window.LP.onHex ? window.LP.onHex(swatch) : null;
      if (hex) this.style.setProperty('--lp-video-buy-tint', swatch); else this.style.removeProperty('--lp-video-buy-tint');
      if (on) { this.style.setProperty('--lp-video-buy-text', on.text); this.style.setProperty('--lp-video-buy-accent', on.accent); }
      else { this.style.removeProperty('--lp-video-buy-text'); this.style.removeProperty('--lp-video-buy-accent'); }
      this.classList.toggle('has-tint', hex);
      if (this.motif) this.motif.colours();
    }
    /* size guide: the active fit's chart, the picked size's row marked */
    guideChart(key) { return this.guide ? this.guide.querySelector('[data-lp-guide-chart="' + key + '"]') : null; }
    openGuide(panel) {
      var chart = this.guideChart(panel.index + '-' + panel.fit); if (!chart || !this.guide) return;
      slice.call(this.guide.querySelectorAll('[data-lp-guide-chart]')).forEach(function (c) { c.hidden = c !== chart; });
      var picked = panel.size ? shortSize(panel.size) : '';
      slice.call(chart.querySelectorAll('tr[data-size]')).forEach(function (r) { r.classList.toggle('is-current', !!picked && r.dataset.size === picked); });
      if (this.guide.showModal) this.guide.showModal(); else this.guide.setAttribute('open', '');
    }

    /* the store's cart-value discount: the highest tier the cents reach */
    tierPct(cents) {
      var pct = 0; (this.valueTiers || []).forEach(function (t) { if (cents >= t.at && t.pct > pct) pct = t.pct; });
      return pct;
    }

    /* the active family is the one whose panel is under the screen's middle line (nearest edge wins between
       families); read on every scroll frame so a fast fling settles on the right one. The clip cards (mobile) use
       an IntersectionObserver to know when they are on screen. */
    watch() {
      var self = this;
      this.onTrackScroll = function (e) { if (!e.target.hasAttribute || !e.target.hasAttribute('data-lp-track')) return; var fam = e.target.closest('[data-lp-family]'); var p = fam && self.panels[self.families.indexOf(fam)]; if (p) p.gallerySync(); };
      this.addEventListener('scroll', this.onTrackScroll, true);
      this.onScroll = function () { if (self.raf) return; self.raf = requestAnimationFrame(function () { self.raf = 0; self.track(); }); };
      this.onWheelBound = function (e) { self.onWheel(e); };
      this.onArrowBound = function (e) { self.onArrow(e); };
      if (!this.static) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('wheel', this.onWheelBound, { passive: false });
        window.addEventListener('keydown', this.onArrowBound);
        this.track();
      }
      if (!('IntersectionObserver' in window)) { this.stageInView = true; this.playStage(); this.families.forEach(function (f) { f.dataset.lpOn = '1'; }); this.playClips(); return; }
      /* .is-on while any part of the section is near the screen: the tint drift and the particle layers (CSS) run
         only then, so bands scrolled past stop painting (01/10/2026 perf) */
      this.onIO = new IntersectionObserver(function (entries) { entries.forEach(function (e) { self.classList.toggle('is-on', e.isIntersecting); if (self.motif) { if (e.isIntersecting) self.motif.start(); else self.motif.stop(); } }); }, { rootMargin: '15% 0px' });
      this.onIO.observe(this);
      this.familyIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.dataset.lpOn = e.isIntersecting ? '1' : ''; });
        self.playClips();
      }, { threshold: 0.35 });
      this.families.forEach(function (f) { self.familyIO.observe(f); });
      var stage = this.querySelector('[data-lp-stage]');
      this.stageIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { self.stageInView = e.isIntersecting; if (e.isIntersecting) self.playStage(); else if (self.video) self.video.pause(); });
      }, { threshold: 0.3 });
      if (stage) this.stageIO.observe(stage);
    }
    /* ---- desktop paging: inside the section one wheel or key gesture moves exactly one family, eased slow →
       fast → slow (power2.inOut), then the page rests; the next family needs a new gesture. Above the first family
       or below the last, scrolling is native. GSAP core only. ---- */
    /* the header (Impact store-header, sticky + hide-on-scroll) is hidden after paging down and shown after paging
       up, so a panel lands under the header only when paging up; --sticky-area-height is a calc() string, so the
       header's measured --header-height is used */
    headerH() { return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0; }
    /* a panel always lands on its own top: each panel carries the header's height as top padding, so the content
       sits in the same place whether the header is shown (after paging up) or hidden (after paging down) */
    familyTops() {
      var y = window.pageYOffset;
      return this.families.map(function (f) { return Math.round(f.getBoundingClientRect().top + y); });
    }
    pageTarget(dir) {
      var tops = this.familyTops(), y = window.pageYOffset, vh = window.innerHeight;
      /* a full screen of runway above the first panel, so the way in reads as a deliberate glide; half a screen below the last */
      if (y < tops[0] - vh || y > tops[tops.length - 1] + vh * 0.5) return null; /* outside the section: native */
      var at = -1;
      tops.forEach(function (t, i) { if (Math.abs(t - y) < 3) at = i; });
      if (at >= 0) { var n = at + dir; return n >= 0 && n < tops.length ? tops[n] : null; }
      /* between panels (arriving from outside or after a drag): go to the next panel edge in that direction */
      var cands = tops.filter(function (t) { return dir > 0 ? t > y + 2 : t < y - 2; });
      return cands.length ? (dir > 0 ? cands[0] : cands[cands.length - 1]) : null;
    }
    pageTo(target) {
      var self = this, pos = { y: window.pageYOffset }, tops = this.familyTops();
      /* from a resting panel: the 0.9s page; from anywhere else (the way in, or after a drag): a longer glide */
      var resting = tops.some(function (t) { return Math.abs(t - pos.y) < 3; });
      this.locked = true; this.acc = 0;
      if (this.pageTween) this.pageTween.kill();
      this.pageTween = this.gsap.to(pos, { y: target, duration: resting ? 0.9 : 1.15, ease: 'power2.inOut',
        onUpdate: function () { window.scrollTo({ top: pos.y, behavior: 'instant' }); }, /* instant: the theme's smooth scroll-behavior must not double-ease it */
        onComplete: function () { self.track(); setTimeout(function () { self.locked = false; self.acc = 0; }, 300); } });
    }
    onWheel(e) {
      if (!this.gsap || !motionOK() || !this.desktop.matches || this.sheetFam) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; /* a sideways swipe is not a page gesture */
      var dir = e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0; if (!dir) return;
      var target = this.pageTarget(dir); if (target === null) return; /* at the section's edge: native */
      /* inside the section the page never scrolls natively: every notch is swallowed, so a trackpad's tiny opening
         deltas cannot nudge the page before the ease starts (that nudge is the shake) */
      e.preventDefault();
      if (this.locked) return;
      var now = Date.now(); if (now - (this.accAt || 0) > 160) this.acc = 0; this.accAt = now;
      this.acc += e.deltaY;
      if (Math.abs(this.acc) < 10) return;
      this.pageTo(target);
    }
    onArrow(e) {
      if (!this.gsap || !motionOK() || !this.desktop.matches || this.sheetFam) return;
      var tag = (e.target.tagName || '').toLowerCase(); if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
      var dir = (e.key === 'ArrowDown' || e.key === 'PageDown') ? 1 : (e.key === 'ArrowUp' || e.key === 'PageUp') ? -1 : 0; if (!dir) return;
      var target = this.pageTarget(dir); if (target === null) return;
      e.preventDefault();
      if (this.locked) return;
      this.pageTo(target);
    }
    track() {
      var mid = window.innerHeight / 2, best = -1, dist = Infinity;
      this.families.forEach(function (f, i) {
        var r = f.getBoundingClientRect();
        var d = r.top <= mid && r.bottom >= mid ? 0 : Math.min(Math.abs(r.top - mid), Math.abs(r.bottom - mid));
        if (d < dist) { dist = d; best = i; }
      });
      if (best >= 0) this.setActive(best);
    }
    setActive(i) {
      if (i === this.active || i < 0 || i >= this.families.length) return;
      this.active = i;
      this.families.forEach(function (f, k) { f.classList.toggle('is-active', k === i); });
      var ap = this.panels[i], acur = ap && ap.current(); if (acur) { this.tint(acur.c.swatch); if (window.LP) window.LP.emit('selection', ap.selection()); }
      var motif = this.querySelector('.lp-video-buy__motif'); if (motif) { motif.dataset.lpMotif = this.families[i].dataset.motif || 'none'; if (this.motif) { this.motif.setKind(motif.dataset.lpMotif); this.motif.colours(); } }
      if (!this.desktop.matches) return;
      this.swapStage();
    }
    /* veil up, swap to whatever family is active by then, veil down; if the target moved meanwhile, go again */
    swapStage() {
      var self = this, g = this.gsap;
      if (!(g && motionOK() && this.veil)) { this.loadStage(this.active); this.playStage(); return; }
      if (this.tl && this.tl.isActive()) { this.pendingSwap = true; return; }
      this.pendingSwap = false;
      this.tl = g.timeline({ defaults: { ease: 'expo.inOut' }, onComplete: function () { var stale = self.video.dataset.lpLoaded !== self.families[self.active].dataset.src; self.pendingSwap = false; if (stale) self.swapStage(); } })
        .set(this.veil, { transformOrigin: 'bottom' })
        .to(this.veil, { scaleY: 1, duration: 0.4 })
        .add(function () { self.loadStage(self.active); self.playStage(); })
        .set(this.veil, { transformOrigin: 'top' })
        .to(this.veil, { scaleY: 0, duration: 0.5 }, '+=0.05');
    }

    /* ---- desktop stage ---- */
    loadStage(i) {
      var f = this.families[i], v = this.video; if (!f || !v) return;
      var src = f.dataset.src; if (!src || v.dataset.lpLoaded === src) return;
      v.dataset.lpLoaded = src; v.poster = f.dataset.poster || ''; v.setAttribute('aria-label', f.dataset.alt || 'Video'); v.src = src; v.load();
      var bar = this.querySelector('[data-lp-bar]'); if (bar) bar.style.width = '0';
    }
    playStage() {
      var v = this.video, self = this;
      if (!v || !this.desktop.matches || !this.stageInView) return;
      if (!this.wantSound && (!motionOK() || !v.getAttribute('poster'))) return;
      this.loadStage(this.active);
      v.muted = !this.wantSound;
      v.play().catch(function () { if (!v.muted) { v.muted = true; v.play().catch(function () {}); } });
    }
    setSound(on) {
      var b = this.querySelector('[data-lp-sound]'), v = this.video;
      this.wantSound = on;
      if (v) v.muted = !on;
      if (b) { b.setAttribute('aria-pressed', on ? 'true' : 'false'); var l = b.querySelector('span'); if (l) l.textContent = on ? b.dataset.on : b.dataset.off; }
      if (on && v) { this.stageInView = true; this.loadStage(this.active); v.play().catch(function () {}); }
    }

    /* ---- mobile clip cards ---- */
    playClips() {
      var self = this, viewing = false;
      this.families.forEach(function (f) {
        var v = f.querySelector('.lp-video-buy__clip-video'), card = f.querySelector('[data-lp-clip]'); if (!v || !card) return;
        var want = (self.static || !self.desktop.matches) && !viewing && motionOK() && f.dataset.lpOn === '1' && !!v.getAttribute('poster') && f.dataset.lpPaused !== '1' && !(self.viewer && self.viewer.open);
        if (want) { if (!v.getAttribute('src')) v.src = v.dataset.src; v.play().then(function () { card.classList.add('is-playing'); }).catch(function () {}); }
        else { v.pause(); card.classList.remove('is-playing'); if (f.dataset.lpOn !== '1') { v.muted = true; slice.call(f.querySelectorAll('[data-lp-clip-sound]')).forEach(function (sb) { sb.setAttribute('aria-pressed', 'false'); }); } }
      });
    }
    /* ---- the form: inline open dialog on desktop, modal bottom sheet on mobile ---- */
    syncForms() {
      var self = this;
      this.families.forEach(function (f) {
        var form = f.querySelector('[data-lp-form]'); if (!form) return;
        if (self.desktop.matches || self.static) { if (self.sheetFam === f) self.closeSheet(); if (!form.open) form.setAttribute('open', ''); var p = self.panels[self.families.indexOf(f)]; if (p) { p.checkNext(); p.placeRing(false); p.placeThumb(false); } }
        else if (form.open && self.sheetFam !== f) { form.removeAttribute('open'); }
      });
    }
    openSheet(fam) {
      if (this.desktop.matches || this.static) return;
      this.closeSheet();
      var form = fam.querySelector('[data-lp-form]'); if (!form || !form.showModal) return;
      this.sheetFam = fam;
      form.showModal(); form.scrollTop = 0;
      var panel = this.panels[this.families.indexOf(fam)]; if (panel) { panel.checkNext(); panel.placeRing(false); }
      this.scrollLock = document.body.style.overflow; document.body.style.overflow = 'hidden';
    }
    closeSheet() {
      var fam = this.sheetFam; if (!fam) return;
      this.sheetFam = null;
      var form = fam.querySelector('[data-lp-form]'); if (form && form.open) form.close();
      document.body.style.overflow = this.scrollLock || '';
      var btn = fam.querySelector('[data-lp-open-sheet]'); if (btn && !this.desktop.matches) btn.focus({ preventScroll: true });
    }
    toggleClipSound(fam) {
      var v = fam.querySelector('.lp-video-buy__clip-video'); if (!v) return;
      var on = v.muted;
      /* one clip with sound at a time */
      this.families.forEach(function (f) { var o = f.querySelector('.lp-video-buy__clip-video'); if (o && o !== v) { o.muted = true; slice.call(f.querySelectorAll('[data-lp-clip-sound]')).forEach(function (ob) { ob.setAttribute('aria-pressed', 'false'); }); } });
      v.muted = !on;
      slice.call(fam.querySelectorAll('[data-lp-clip-sound]')).forEach(function (b) { b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
      if (on) { fam.dataset.lpOn = '1'; fam.dataset.lpPaused = ''; if (!v.getAttribute('src')) v.src = v.dataset.src; v.play().catch(function () {}); }
    }
    /* ---- static layout: clip controls, clip placement, viewer ---- */
    toggleClipPlay(fam) {
      var v = fam.querySelector('.lp-video-buy__clip-video'); if (!v) return;
      if (v.paused) { fam.dataset.lpPaused = ''; fam.dataset.lpOn = '1'; if (!v.getAttribute('src')) v.src = v.dataset.src; v.play().catch(function () {}); }
      else { fam.dataset.lpPaused = '1'; v.pause(); }
    }
    syncClip(fam) {
      var v = fam.querySelector('.lp-video-buy__clip-video'), card = fam.querySelector('[data-lp-clip]'); if (!v || !card) return;
      card.classList.toggle('is-playing', !v.paused);
      card.classList.toggle('is-paused', v.paused);
      var t = fam.querySelector('[data-lp-clip-toggle]'); if (t) t.setAttribute('aria-label', v.paused ? 'Play' : 'Pause');
      slice.call(fam.querySelectorAll('[data-lp-clip-sound]')).forEach(function (b) { b.setAttribute('aria-pressed', v.muted ? 'false' : 'true'); });
    }
    /* static + phone: the clip is the photo gallery's first slide; elsewhere it sits in the media block */
    placeClips() {
      var self = this;
      if (!this.static) return;
      this.families.forEach(function (f) {
        var clip = f.querySelector('[data-lp-clip]'), media = f.querySelector('.lp-video-buy__media'), track = f.querySelector('[data-lp-track]'); if (!clip || !media || !track) return;
        var controls = f.querySelector('.lp-video-buy__controls'), chip = f.querySelector('.lp-video-buy__chip'), p = self.panels[self.families.indexOf(f)];
        if (!self.desktop.matches) { if (clip.parentNode !== track) { track.insertBefore(clip, track.firstChild); track.scrollTo({ left: 0, behavior: 'auto' }); p && (p.slide = 0); } /* the snap track had settled on the first photo; start on the clip */ if (controls && controls.parentNode !== f.querySelector('[data-lp-photo]')) { f.querySelector('[data-lp-photo]').appendChild(controls); } if (chip && chip.parentNode !== f.querySelector('[data-lp-photo]')) f.querySelector('[data-lp-photo]').appendChild(chip); }
        else { if (clip.parentNode !== media) media.insertBefore(clip, media.firstChild); if (controls && controls.parentNode !== media) media.insertBefore(controls, clip.nextSibling); if (chip && chip.parentNode !== media) media.insertBefore(chip, clip.nextSibling); }
        if (p) p.gallerySync();
      });
    }
    openViewer(fam) {
      if (!this.viewer || !this.viewer.showModal) return;
      var v = fam.querySelector('.lp-video-buy__clip-video'); if (!v) return;
      this.viewer.style.setProperty('--lp-video-buy-ar', fam.style.getPropertyValue('--lp-video-buy-clip') || '9 / 16');
      this.viewer.setAttribute('aria-label', fam.dataset.alt || 'Video');
      v.pause();
      this.full.src = v.dataset.src; this.full.poster = fam.dataset.poster || ''; this.full.muted = false; this.full.currentTime = v.currentTime || 0;
      var m = this.querySelector('[data-lp-mute]'); if (m) m.setAttribute('aria-pressed', 'false');
      document.body.style.overflow = 'hidden';
      this.viewer.showModal();
      this.full.play().catch(function () {});
    }
    closeViewer() { if (this.viewer && this.viewer.open) this.viewer.close(); }
  }
  window.customElements.define('lp-video-buy', LpVideoBuy);
})();
